import { useState, useEffect, useRef } from "react";

import { FiMenu, FiX } from "react-icons/fi";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import Logo from "./Logo";
import NavLinks from "./NavLinks";
import UserMenu from "./UserMenu";
import IconButtons from "./IconButtons";
import Footer from "../Footer/Footer";
import api from "../../api";

export default function MobileHeader({
  user,
  setUser,
  isLoggedIn,
  setShowModal,
  location,
  setNotifOpen,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [isFixed, setIsFixed] = useState(false);

  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const navigate = useNavigate();

  // =========================
  // SCROLL FIX
  // =========================

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }

    const handleScroll = () => {
      setIsFixed(window.scrollY > headerHeight);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [headerHeight]);

  // =========================
  // SEARCH
  // =========================

  const handleSearch = async (value) => {
    setSearch(value);

    if (!value.trim()) {
      setResults([]);
      return;
    }

    try {
      const res = await api.get(
        `/api/products/search/?q=${encodeURIComponent(value)}`
      );

      const data = Array.isArray(res.data) ? res.data : [];

      setResults(data);
    } catch (err) {
      console.error("Mobile search error:", err);
      setResults([]);
    }
  };

  // =========================
  // GET PRODUCT IMAGE
  // =========================

  const getProductImage = (product) => {
    if (!product) {
      return "/placeholder.jpg";
    }

    // Sub images
    if (
      Array.isArray(product.sub_images) &&
      product.sub_images.length > 0
    ) {
      const image = product.sub_images[0];

      if (typeof image === "string") {
        return image;
      }

      if (image?.image) {
        return image.image;
      }

      if (image?.url) {
        return image.url;
      }
    }

    // Main image
    if (product.image) {
      return product.image;
    }

    if (product.product_image) {
      return product.product_image;
    }

    if (product.main_image) {
      return product.main_image;
    }

    if (product.image_url) {
      return product.image_url;
    }

    return "/placeholder.jpg";
  };

  // =========================
  // GET PRODUCT PRICE
  // =========================

  const getProductPrice = (product) => {
    if (!product) {
      return null;
    }

    // First check size prices
    if (
      Array.isArray(product.sizes) &&
      product.sizes.length > 0
    ) {
      const prices = product.sizes
        .map((size) => Number(size?.price))
        .filter((price) => Number.isFinite(price));

      if (prices.length > 0) {
        return Math.min(...prices);
      }
    }

    // Otherwise use normal product price
    if (
      product.price !== undefined &&
      product.price !== null &&
      product.price !== ""
    ) {
      const price = Number(product.price);

      if (Number.isFinite(price)) {
        return price;
      }
    }

    return null;
  };

  // =========================
  // OPEN PRODUCT
  // =========================

  const handleProductClick = (product) => {
    if (!product?.id) {
      console.error("Product ID missing:", product);
      return;
    }

    navigate(`/product/${product.id}`);

    setSearch("");
    setResults([]);
    setMenuOpen(false);
  };

  // =========================
  // RETURN
  // =========================

  return (
    <>
      {/* =========================
          HEADER SPACER
      ========================= */}

      {isFixed && (
        <div style={{ height: headerHeight }} />
      )}

      {/* =========================
          HEADER
      ========================= */}

      <header
        ref={headerRef}
        className={`w-full border-b border-[#D7E6E4] transition-all duration-300 ${
          isFixed
            ? "fixed top-0 left-0 bg-[#F7F3EA]/95 backdrop-blur-md shadow-md z-50"
            : "relative bg-[#F7F3EA]"
        }`}
      >
        {/* =========================
            TOP BAR
        ========================= */}

        <div className="h-16 flex items-center justify-between px-4">

          {/* LEFT */}

          <div className="flex items-center gap-2">

            <button
              onClick={() => setMenuOpen(true)}
              type="button"
              className="text-[#0F5C5E] hover:text-[#0B4648] transition"
              aria-label="Open menu"
            >
              <FiMenu className="text-xl" />
            </button>

            <Logo />

          </div>

          {/* RIGHT ICONS */}

          <div className="flex items-center gap-4 text-xl text-[#0F5C5E]">

            <IconButtons
              setNotifOpen={setNotifOpen}
            />

            <UserMenu
              isLoggedIn={isLoggedIn}
              user={user}
              setUser={setUser}
              setShowModal={setShowModal}
            />

          </div>

        </div>

        {/* =========================
            SEARCH BAR
        ========================= */}

        <div className="px-4 pb-3 relative">

          <div className="relative">

            <input
              type="text"
              value={search}
              onChange={(e) =>
                handleSearch(e.target.value)
              }
              placeholder="Search products..."
              className="
                w-full
                border
                border-[#B8D0CE]
                bg-white
                text-[#173B3C]
                rounded-lg
                py-2
                pl-3
                pr-10
                text-sm
                placeholder:text-[#759392]
                focus:outline-none
                focus:border-[#0F5C5E]
                focus:ring-2
                focus:ring-[#0F5C5E]/20
              "
            />

            <FaSearch
              className="
                absolute
                right-3
                top-2.5
                text-[#0F5C5E]
              "
            />

          </div>

          {/* =========================
              SEARCH RESULTS
          ========================= */}

          {results.length > 0 && (

            <div
              className="
                absolute
                left-4
                right-4
                bg-white
                shadow-lg
                rounded-lg
                mt-2
                max-h-80
                overflow-y-auto
                z-50
                border
                border-[#D7E6E4]
              "
            >

              {results.map((item) => {

                const image = getProductImage(item);
                const price = getProductPrice(item);

                return (

                  <div
                    key={item.id}
                    onClick={() =>
                      handleProductClick(item)
                    }
                    className="
                      flex
                      items-center
                      gap-3
                      p-3
                      hover:bg-[#EAF3F1]
                      cursor-pointer
                      border-b
                      border-[#D7E6E4]
                      last:border-b-0
                    "
                  >

                    {/* PRODUCT IMAGE */}

                    <img
                      src={image}
                      alt={item.name || "Product"}
                      className="
                        w-14
                        h-14
                        rounded-lg
                        object-cover
                        flex-shrink-0
                      "
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src =
                          "/placeholder.jpg";
                      }}
                    />

                    {/* PRODUCT INFO */}

                    <div className="min-w-0 flex-1">

                      <p
                        className="
                          font-semibold
                          text-sm
                          truncate
                          text-[#173B3C]
                        "
                      >
                        {item.name}
                      </p>

                      {price !== null ? (

                        <p
                          className="
                            text-[#0F5C5E]
                            font-bold
                            text-sm
                          "
                        >
                          ${price.toFixed(2)}
                        </p>

                      ) : (

                        <p className="text-[#759392] text-sm">
                          Price unavailable
                        </p>

                      )}

                    </div>

                  </div>

                );
              })}

            </div>

          )}

        </div>

      </header>

      {/* =========================
          OVERLAY
      ========================= */}

      <div
        className={`
          fixed
          inset-0
          bg-[#173B3C]/50
          transition-opacity
          duration-300
          ${
            menuOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }
        `}
        onClick={() => setMenuOpen(false)}
      />

      {/* =========================
          SIDE MENU
      ========================= */}

      <div
        className={`
          fixed
          top-0
          left-0
          h-screen
          w-screen
          bg-[#F7F3EA]
          shadow-2xl
          transform
          transition-transform
          duration-300
          z-50
          ${
            menuOpen
              ? "translate-x-0"
              : "-translate-x-full"
          }
        `}
      >

        {/* =========================
            TOP BAR
        ========================= */}

        <div
          className="
            h-16
            bg-[#0F5C5E]
            flex
            items-center
            justify-between
            px-4
          "
        >

          <Logo />

          <button
            onClick={() => setMenuOpen(false)}
            type="button"
            className="
              text-white
              hover:text-[#C7A86B]
              transition
            "
            aria-label="Close menu"
          >
            <FiX className="text-2xl" />
          </button>

        </div>

        {/* =========================
            CONTENT
        ========================= */}

        <div
          className="
            mt-4
            px-6
            flex
            flex-col
            gap-6
            overflow-y-auto
            h-[calc(100%-64px)]
          "
        >

          <NavLinks
            isMobile={true}
            location={location}
            onLinkClick={() => setMenuOpen(false)}
          />

          <Footer />

        </div>

      </div>
    </>
  );
}